import {
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetMessagesQueryFilters,
  MESSAGE_ARRANGE_BY_FILTER_KEYS,
  MessageStatus,
  MessageType,
} from '@schooly/api';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export type UseMessagesFiltersProps = {
  schoolId: string;
  filters: GetMessagesQueryFilters;
  onSetFilters: (filters: GetMessagesQueryFilters) => void;
};

export const useMessagesFilters = ({
  schoolId,
  filters,
  onSetFilters,
}: UseMessagesFiltersProps) => {
  const { formatMessage } = useIntl();

  const actions = useMemo(() => {
    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const onSetMessageStatus = (v: MessageStatus[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.MessagesStatus]: v,
      });
    };

    const onSetMessageType = (v: MessageType[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.MessagesType]: v,
      });
    };

    const onSetCreator = (v: string[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Creator]: v,
      });
    };

    return {
      onSetDate: (v: [Date, Date]) =>
        onSetFilters({
          ...filters,
          [FilterKeys.Date]: [
            format(v[0], DEFAULT_DATE_FORMAT_FNS),
            format(v[1], DEFAULT_DATE_FORMAT_FNS),
          ],
        }),
      onSetMessageStatus,
      onClearMessageStatus: clearFilter(FilterKeys.MessagesStatus),
      onSetMessageType,
      onClearMessageType: clearFilter(FilterKeys.MessagesType),
      onSetCreator,
      onClearCreator: clearFilter(FilterKeys.Creator),
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date;
  const filtersStatus = filters.message_status;
  const filtersType = filters.message_type;
  const filtersCreator = filters.creator;

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-Period' }),
    [FilterKeys.MessagesStatus]: formatMessage({ id: 'messages-Status' }),
    [FilterKeys.MessagesType]: formatMessage({ id: 'messages-Type' }),
    [FilterKeys.Creator]: formatMessage({ id: 'messages-From' }),
    arrangeBy: formatMessage({ id: 'filter-ArrangeBy' }),
  };

  const arrangeByDropdownOptions = MESSAGE_ARRANGE_BY_FILTER_KEYS;

  return {
    ...actions,
    labels,
    arrangeByDropdownOptions,
    filtersDate,
    filtersStatus,
    filtersType,
    filtersCreator,
  };
};
